const DEFAULT_BASE_URL = 'https://app.biel.ai/api/v1';

export class BielService {
  private project: string;
  private apiKey: string;
  private errorMessage404: string;
  private errorMessage403: string;
  private errorMessageDefault: string;
  private baseUrl: string;

  constructor(
    project: string,
    errorMessage404: string,
    errorMessage403: string,
    errorMessageDefault: string,
    apiKey: string = '',
    baseUrl: string = ''
  ) {
    this.project = project;
    this.apiKey = apiKey;
    this.errorMessage404 = errorMessage404;
    this.errorMessage403 = errorMessage403;
    this.errorMessageDefault = errorMessageDefault;
    this.baseUrl = baseUrl || DEFAULT_BASE_URL;
  }

  private getHeaders(includeContentType: boolean = true): HeadersInit {
    const headers: HeadersInit = {};

    if (includeContentType) {
      headers['Content-Type'] = 'application/json; charset=UTF-8';
    }

    if (this.apiKey) {
      headers['Authorization'] = `Api-Key ${this.apiKey}`;
    }

    return headers;
  }

  async getProjectDetails(): Promise<
    | {
        whitelabel: boolean;
        suggestedQuestions: string[];
        initialMessage: string;
        filters: { position: number; title: string; url: string }[];
      }
    | string
  > {
    try {
      const response = await fetch(`${this.baseUrl}/projects/${this.project}/`, {
        headers: this.getHeaders(false),
      });

      if (response.status === 404) {
        return this.errorMessage404;
      }

      if (response.status === 403) {
        return this.errorMessage403;
      }

      if (!response.ok) {
        return this.errorMessageDefault;
      }

      const data = await response.json();
      return {
        whitelabel: data.whitelabel,
        suggestedQuestions: data.suggested_questions,
        initialMessage: data.initial_message,
        filters: data.filters,
      };
    } catch (error) {
      console.error('Error fetching project details:', error);
      return this.errorMessageDefault;
    }
  }

  async sendFeedback(messageId: string, feedback: { reaction: 0 | 1 }) {
    try {
      const response = await fetch(`${this.baseUrl}/messages/${messageId}/feedback/`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(feedback),
      });

      if (!response.ok) {
        console.error('Error:', response.status);
      }
    } catch (error) {
      console.error('Error sending feedback:', error);
    }
  }

  async sendQuery(
    q: string,
    domain_filter: string
  ): Promise<{ title: string; fragment: string; url: string }[]> {
    try {
      const response = await fetch(
        `${this.baseUrl}/projects/${this.project}/search/?q=${q}&domain_filter=${domain_filter}&url=${window.location.href}`,
        { headers: this.getHeaders(false) }
      );

      if (!response.ok) {
        console.error('Error:', response.status);
        return [];
      }

      const textData = await response.text();
      if (!textData) {
        return [];
      }

      const data = JSON.parse(textData);

      return data;
    } catch (error) {
      console.error('Error sending query:', error);
      return [];
    }
  }

  async searchClicked() {
    try {
      const response = await fetch(`${this.baseUrl}/projects/${this.project}/search/click/`, {
        method: 'POST',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        console.error('Error:', response.status);
      }
    } catch (error) {
      console.error('Error sending search clicked:', error);
    }
  }
}
